/**
 * A bucket's settings (Tenant Admin): rename it, replace which groups can
 * read it, or delete it once empty. Write access is the platform's authoring
 * rule and deliberately has no setting here.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, Input } from '@servicecompressionllc/sc-design-system';
import { knowledgeApi, type KnowledgeBucket } from './api.js';

export const BucketSettingsTab = ({ bucket }: { bucket: KnowledgeBucket }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(bucket.name);
  const [readGroupIds, setReadGroupIds] = useState<string[]>(bucket.readGroups.map((g) => g.id));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const groups = useQuery({
    queryKey: ['knowledge', 'bucketGroups'],
    queryFn: knowledgeApi.groups,
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['knowledge', 'buckets'] });
  const save = useMutation({
    mutationFn: () =>
      knowledgeApi.updateBucket(bucket.id, {
        name: name.trim(),
        ...(bucket.isDefault ? {} : { readGroupIds }),
      }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => knowledgeApi.deleteBucket(bucket.id),
    onSuccess: () => {
      invalidate();
      void navigate('/knowledge/rules');
    },
  });

  const toggleGroup = (id: string) =>
    setReadGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  const dirty =
    name.trim() !== bucket.name ||
    (!bucket.isDefault &&
      JSON.stringify([...readGroupIds].sort()) !==
        JSON.stringify(bucket.readGroups.map((g) => g.id).sort()));

  return (
    <div className="flex max-w-xl flex-col gap-5">
      {!bucket.isDefault && (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bucket-settings-name"
            className="text-xs font-semibold uppercase tracking-wide text-ink-subtle"
          >
            Name
          </label>
          <Input
            id="bucket-settings-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Bucket name"
            className="w-full"
          />
          <span className="text-xs text-ink-subtle">
            Renaming never affects the bucket’s content or access.
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Which groups can read it
        </span>
        {bucket.isDefault ? (
          <p className="text-sm text-ink-muted">
            The General bucket is readable by everyone who can search the knowledge base, and can’t
            be restricted. Content for a narrower audience belongs in its own bucket.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2 rounded-md border border-outline bg-surface p-3">
              {(groups.data?.groups ?? []).map((g) => (
                <label key={g.id} className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={readGroupIds.includes(g.id)}
                    onChange={() => toggleGroup(g.id)}
                    aria-label={g.name}
                  />
                  <span className="text-sm text-ink">{g.name}</span>
                  <span className="text-xs text-ink-subtle">
                    {g.memberCount} {g.memberCount === 1 ? 'member' : 'members'}
                  </span>
                </label>
              ))}
            </div>
            <span className="text-xs text-ink-subtle">
              With no groups selected, everyone who can search the knowledge base can read it.
            </span>
          </>
        )}
      </div>

      {(save.error ?? remove.error) !== null && (
        <Banner variant="danger">
          <Banner.Description>{((save.error ?? remove.error) as Error).message}</Banner.Description>
        </Banner>
      )}

      {!bucket.isDefault && (
        <div className="flex items-center gap-2">
          <Button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending || name.trim().length === 0}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {!bucket.isDefault &&
            (confirmDelete ? (
              <span className="ml-auto flex items-center gap-2">
                <span className="text-sm text-ink-muted">
                  Delete this bucket? It must be empty.
                </span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                  Keep it
                </Button>
              </span>
            ) : (
              <Button variant="ghost" className="ml-auto" onClick={() => setConfirmDelete(true)}>
                Delete bucket
              </Button>
            ))}
        </div>
      )}
    </div>
  );
};
